import { PanelToolbar } from '../../components/panels/PanelToolbar';
import { LogTrackPanel } from '../../components/panels/LogTrackPanel';
import type { Track } from '../../components/panels/LogTrackPanel';

/** Live channel readings, two to a row, as on the RT Time Log screen. */
const READOUTS: { label: string; value: string; unit: string }[] = [
  { label: 'TVD', value: '2672.46', unit: 'm' },
  { label: 'Lag Depth', value: '2673.37', unit: 'm' },
  { label: 'Lag Time', value: '-999.25', unit: 'min' },
  { label: 'Block Position', value: '4.47', unit: 'm' },
  { label: 'Hookload', value: '77.80', unit: 'klb' },
  { label: 'ROP', value: '2.50', unit: 'm/h' },
  { label: 'Torque', value: '0.00', unit: 'klbf-ft' },
  { label: 'RPM', value: '0.00', unit: 'rpm' },
  { label: 'Mud Flow In', value: '0.00', unit: 'gpm' },
  { label: 'Mud Flow Out', value: '10.00', unit: '%' },
  { label: 'Mud Density In', value: '19.49', unit: 'ppg' },
  { label: 'Mud Density Out', value: '19.40', unit: 'ppg' },
  { label: 'Mud Temperature In (avg)', value: '46.21', unit: 'degC' },
  { label: 'Mud Temperature Out', value: '54.36', unit: 'degC' },
  { label: 'Pit Active', value: '945.52', unit: 'bbl' },
  { label: 'Gain/Loss', value: '17.01', unit: 'bbl' },
  { label: 'Trip Tank 1 Vol', value: '39.00', unit: 'bbl' },
  { label: 'Trip Tank 1 Volume', value: '39.00', unit: 'bbl' },
  { label: 'Total Gas', value: '0.00', unit: '%' },
  { label: 'SPP', value: '0.00', unit: 'psi' },
  { label: 'Choke Pressure', value: '0.00', unit: 'psi' },
  { label: 'Cement Pressure', value: '9.00', unit: 'psi' },
];

const STAMP = '28/09/11\n11:34:55';

/** Rig-state legend chips that sit above the activity track. */
const ACTIVITIES: { label: string; color: string }[] = [
  { label: 'Drill-rotate', color: '#cfe8f7' },
  { label: 'Wash-up', color: '#fde2b8' },
  { label: 'Pull-out-hole', color: '#f7c9c4' },
  { label: 'In-slip', color: '#dfe3e6' },
  { label: 'Drill-slide', color: '#cfe8f7' },
  { label: 'Wash-down', color: '#fde2b8' },
  { label: 'POOH-rotate', color: '#f7c9c4' },
  { label: 'Static', color: '#dfe3e6' },
  { label: 'Ream-up', color: '#d6f0d2' },
  { label: 'Run-in-hole', color: '#d7d2f0' },
  { label: 'Circ-only', color: '#f5d8ef' },
  { label: 'Rotate-only', color: '#e3e8c9' },
  { label: 'Ream-down', color: '#d6f0d2' },
  { label: 'RIH-rotate', color: '#d7d2f0' },
  { label: 'Circ-rotate', color: '#f5d8ef' },
  { label: 'Out-of-hole', color: '#cfeeee' },
];

const TRACKS: Track[] = [
  {
    title: 'ROP',
    width: 108,
    scales: [
      { min: '250', unit: 'm/h', max: '0' },
      { name: 'Block Position', min: '0', unit: 'm', max: '50' },
      { name: 'Hookload', min: '0', unit: 'klb', max: '500' },
      { name: 'WOB', min: '0', unit: 'klb', max: '50' },
    ],
  },
  { title: 'HOLE DEPTH', width: 34, vertical: true },
  { title: 'BIT DEPTH', width: 34, vertical: true },
  {
    title: 'Torque',
    width: 108,
    scales: [
      { min: '0', unit: 'klbf-ft', max: '30' },
      { name: 'RPM', min: '0', unit: 'rpm', max: '300' },
      { name: 'MWD Collar RPM', min: '0', unit: 'rpm', max: '300' },
      { name: 'StickSlip', min: '0', unit: 'c/min', max: '150' },
    ],
  },
  {
    title: 'Cement Pressure',
    width: 128,
    scales: [
      { min: '0', unit: 'psi', max: '10000' },
      { name: 'SPP', min: '0', unit: 'psi', max: '5000' },
      { name: 'Casing (Choke) Pressure', min: '0', unit: 'psi', max: '200' },
      { name: 'Mud Flow In', min: '0', unit: 'gpm', max: '2000' },
      { name: 'Mud Flow Out', min: '0', unit: '%', max: '100' },
      { name: 'MPD Flow In', min: '0', unit: 'gpm', max: '2000' },
      { name: 'Surface Back Pressure', min: '0', unit: 'psi', max: '500' },
      { name: 'MWD Ann Press', min: '0', unit: 'psi', max: '10000' },
    ],
  },
  { title: 'LAG DEPTH', width: 34, vertical: true },
];

const RIGHT_TRACKS: Track[] = [
  {
    title: 'Total Gas',
    width: 120,
    scales: [
      { min: '0', unit: '%', max: '5' },
      { name: 'Trip Tank 1 Volume', min: '0', unit: 'bbl', max: '1000' },
      { name: 'Tank Volume (active)', min: '0', unit: 'bbl', max: '1000' },
      { name: 'Tank Volume Change (act)', min: '-300', unit: 'bbl', max: '300' },
    ],
  },
  {
    title: 'Mud Temperature In',
    width: 120,
    scales: [
      { min: '0', unit: 'degC', max: '200' },
      { name: 'Mud Temperature Out', min: '0', unit: 'degC', max: '200' },
      { name: 'Mud Density In', min: '6', unit: 'ppg', max: '16' },
      { name: 'Mud Density Out', min: '6', unit: 'ppg', max: '16' },
      { name: 'PWD Ann ECD', min: '6', unit: 'ppg', max: '16' },
      { name: 'MWD Ann Temp', min: '0', unit: 'degC', max: '200' },
      { name: 'MWD Ann ECD', min: '6', unit: 'ppg', max: '16' },
    ],
  },
];

export function RtTimeLog() {
  return (
    <div className="splitpane splitpane--readouts">
      <section className="readoutgrid" aria-label="Live channel readings">
        {READOUTS.map((r) => (
          <div className="readout-tile" key={r.label}>
            <span className="readout-tile-label">{r.label}</span>
            <span className="readout-tile-stamp">{STAMP}</span>
            <span className="readout-tile-value">{r.value}</span>
            <span className="readout-tile-unit">{r.unit}</span>
          </div>
        ))}
      </section>

      <section className="panel" aria-label="RT Timelog">
        <PanelToolbar title="RT Timelog" accent options={['RT Timelog', 'RT Depth Log']} />

        <div className="activitychips">
          {ACTIVITIES.map((a) => (
            <span className="chip" key={a.label} style={{ background: a.color }}>
              {a.label}
            </span>
          ))}
        </div>

        <LogTrackPanel tracks={[...TRACKS, ...RIGHT_TRACKS]} minHeight={260} />
      </section>
    </div>
  );
}
